import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bauvorhabenApi, gewerkeApi } from '@/services/api';
import { ContactPicker } from '@/components/ContactPicker';
import type { Contact, InvoiceDetail, InvoiceDirection } from '@/types';
import { AlertCircle, Save, X, FileUp } from 'lucide-react';

/**
 * Rechnungs-Stammdatenformular — Anlegen (/rechnungen/new) und
 * Bearbeiten (inline in der Detail-Spalte, admin). Datei wird als
 * File-Objekt mitgegeben und nach dem Create/Update hochgeladen
 * (Multipart erst nach existierender Rechnungs-ID).
 */

export interface InvoiceFormPayload {
  direction: InvoiceDirection;
  invoice_number: string;
  contact_id: number | null;
  bauvorhaben_id: number | null;
  gewerk_id: number | null;
  amount_net: string;
  amount_tax: string;
  amount_gross: string;
  tax_rate_percent: string;
  invoice_date: string;
  due_date: string;
  description: string;
  notes: string;
}

interface InvoiceFormProps {
  isNew: boolean;
  /** InvoiceDetail — Netto/MwSt-Satz/Notizen gibt's nur im Detail (Bearbeiten). */
  initial?: InvoiceDetail | null;
  isSaving: boolean;
  error: string;
  onSave: (payload: InvoiceFormPayload, file: File | null) => void;
  onCancel?: () => void;
}

function contactLabel(contact: Contact): string {
  return (
    contact.display_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
    contact.company_name ||
    'Kontakt'
  );
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function InvoiceForm({ isNew, initial, isSaving, error, onSave, onCancel }: InvoiceFormProps) {
  const [direction, setDirection] = useState<InvoiceDirection>(initial?.direction ?? 'eingang');
  const [invoiceNumber, setInvoiceNumber] = useState(initial?.invoice_number ?? '');
  const [contact, setContact] = useState<Contact | null>(null);
  const [bauvorhabenId, setBauvorhabenId] = useState<string>(initial?.bauvorhaben_id ? String(initial.bauvorhaben_id) : '');
  const [gewerkId, setGewerkId] = useState<string>(initial?.gewerk_id ? String(initial.gewerk_id) : '');
  const [amountNet, setAmountNet] = useState(initial?.amount_net != null ? String(initial.amount_net) : '');
  const [amountTax, setAmountTax] = useState(initial?.amount_tax != null ? String(initial.amount_tax) : '');
  const [amountGross, setAmountGross] = useState(initial?.amount_gross ?? '');
  const [taxRate, setTaxRate] = useState(initial?.tax_rate_percent != null ? String(initial.tax_rate_percent) : '');
  const [invoiceDate, setInvoiceDate] = useState(initial?.invoice_date ?? '');
  const [dueDate, setDueDate] = useState(initial?.due_date ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');

  // Kontakt-Chip für Bearbeiten: ContactPicker liefert volle
  // Contact-Objekte nur beim Suchen — bestehende Zuordnung bleibt
  // unverändert, solange nichts Neues gesucht wird.

  const [bauvorhabenOptions, setBauvorhabenOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [gewerkOptions, setGewerkOptions] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    bauvorhabenApi
      .getAll({ page: 1, per_page: 100, sort: 'name_asc' })
      .then((response) => setBauvorhabenOptions(response.data.data.map((b) => ({ id: b.id, name: b.name }))))
      .catch(() => setBauvorhabenOptions([]));
    gewerkeApi
      .getAll()
      .then((response) =>
        setGewerkOptions(
          response.data.data.phases.flatMap((phase) => phase.gewerke.map((g) => ({ id: g.id, name: g.name })))
        )
      )
      .catch(() => setGewerkOptions([]));
  }, []);

  // Auto-Berechnung: bei gesetztem MwSt-Satz Netto/Steuer aus dem Brutto
  // ableiten (manuelle Eingabe bleibt möglich — nur bei Satzwechsel neu).
  const handleTaxRateChange = (value: string) => {
    setTaxRate(value);
    const gross = parseFloat(amountGross);
    const rate = parseFloat(value);
    if (!Number.isNaN(gross) && !Number.isNaN(rate) && gross > 0) {
      const net = Math.round((gross / (1 + rate / 100)) * 100) / 100;
      setAmountNet(String(net));
      setAmountTax(String(Math.round((gross - net) * 100) / 100));
    }
  };

  const handleFileChange = (next: File | null) => {
    setFileError('');
    if (!next) {
      setFile(null);
      return;
    }
    if (next.size > MAX_FILE_BYTES) {
      setFileError('Datei ist zu groß (maximal 10 MB)');
      return;
    }
    if (next.type !== 'application/pdf') {
      setFileError('Nur PDF-Dateien sind erlaubt');
      return;
    }
    setFile(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoiceNumber.trim()) {
      // Pflichtfeld-Prüfung übernimmt der Backend — aber Nummer ist die
      // auffälligste, hier direkt abfangen ohne Request.
      return;
    }
    const payload: InvoiceFormPayload = {
      direction,
      invoice_number: invoiceNumber.trim(),
      contact_id: contact?.id ?? (initial?.contact_id ?? null),
      bauvorhaben_id: bauvorhabenId ? Number(bauvorhabenId) : null,
      gewerk_id: gewerkId ? Number(gewerkId) : null,
      amount_net: amountNet,
      amount_tax: amountTax,
      amount_gross: amountGross,
      tax_rate_percent: taxRate,
      invoice_date: invoiceDate,
      due_date: dueDate,
      description: description,
      notes: notes,
    };
    onSave(payload, file);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {isNew ? 'Neue Rechnung erfassen' : 'Rechnung bearbeiten'}
      </h2>
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Richtung *</label>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as InvoiceDirection)}
            className="input"
            disabled={!isNew}
          >
            <option value="eingang">Eingangsrechnung</option>
            <option value="ausgang">Ausgangsrechnung</option>
          </select>
          {!isNew && <p className="mt-1 text-xs text-gray-500">Die Richtung kann nachträglich nicht geändert werden.</p>}
        </div>
        <div>
          <label className="label">Rechnungsnummer *</label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className="input"
            required
          />
        </div>

        <div className="sm:col-span-2">
          <label className="label">{direction === 'eingang' ? 'Lieferant' : 'Empfänger'}</label>
          {contact ? (
            <div className="flex items-center gap-2">
              <Link
                to={`/contacts/${contact.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800 hover:text-primary-700"
              >
                {contactLabel(contact)}
              </Link>
              <button
                type="button"
                onClick={() => setContact(null)}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                title="Zuordnung entfernen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : initial?.contact_id ? (
            <p className="text-sm text-gray-600">
              {initial.contact_name || 'Kontaktverknüpfung'} (bleibt bestehen, solange nichts Neues gesucht wird)
            </p>
          ) : (
            <ContactPicker
              onSelect={(c) => setContact(c)}
              placeholder={direction === 'eingang' ? 'Lieferant suchen…' : 'Empfänger suchen…'}
            />
          )}
        </div>

        <div>
          <label className="label">Bauvorhaben</label>
          <select value={bauvorhabenId} onChange={(e) => setBauvorhabenId(e.target.value)} className="input">
            <option value="">— Kein Bauvorhaben —</option>
            {bauvorhabenOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Gewerk</label>
          <select value={gewerkId} onChange={(e) => setGewerkId(e.target.value)} className="input">
            <option value="">— Kein Gewerk —</option>
            {gewerkOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Bruttobetrag (€) *</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amountGross}
            onChange={(e) => setAmountGross(e.target.value)}
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">MwSt-Satz (%)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={taxRate}
            onChange={(e) => handleTaxRateChange(e.target.value)}
            className="input"
            placeholder="z. B. 19"
          />
        </div>
        <div>
          <label className="label">Nettobetrag (€)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountNet}
            onChange={(e) => setAmountNet(e.target.value)}
            className="input"
          />
        </div>
        <div>
          <label className="label">MwSt-Betrag (€)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountTax}
            onChange={(e) => setAmountTax(e.target.value)}
            className="input"
          />
        </div>

        <div>
          <label className="label">Rechnungsdatum *</label>
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">Fällig am</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="input"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="label">Betreff</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
            placeholder="Kurzer Titel der Rechnung"
          />
        </div>

        {isNew && (
          <div className="sm:col-span-2">
            <label className="label">PDF</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              className="input"
            />
            {file && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                <FileUp className="h-3.5 w-3.5" />
                {file.name} ({Math.round(file.size / 1024)} KB)
              </p>
            )}
            {fileError && <p className="mt-1 text-xs text-red-600">{fileError}</p>}
          </div>
        )}

        <div className="sm:col-span-2">
          <label className="label">Notizen</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="input"
          />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={isSaving || !!fileError}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Speichern…' : 'Speichern'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Abbrechen
          </button>
        )}
      </div>
    </form>
  );
}